'use client';

import Icon from '@/components/ui/AppIcon';
import { analytics } from '@/lib/analytics';

const pricingTiers = [
  {
    id: 'express',
    name: 'Express Shipping',
    icon: 'BoltIcon',
    description: 'Fast delivery for time-sensitive shipments',
    basePrice: '$25',
    priceUnit: 'per shipment',
    features: [
      '2-3 business days delivery',
      'Real-time tracking',
      'Priority handling',
      'Door-to-door service',
      'Insurance included (up to $500)',
      'Signature confirmation',
    ],
    popular: false,
    color: 'primary',
  },
  {
    id: 'freight',
    name: 'Freight Services',
    icon: 'public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png',
    description: 'Cost-effective solutions for large shipments',
    basePrice: '$150',
    priceUnit: 'per pallet',
    features: [
      'Bulk shipping discounts',
      'Flexible scheduling',
      'Customs support',
      'Dedicated account manager',
      'Insurance included (up to $5,000)',
      'White glove service available',
    ],
    popular: true,
    color: 'secondary',
  },
  {
    id: 'warehousing',
    name: 'Warehousing',
    icon: 'BuildingStorefrontIcon',
    description: 'Secure storage with inventory management',
    basePrice: '$0.50',
    priceUnit: 'per sq ft/month',
    features: [
      'Climate-controlled facilities',
      '24/7 security monitoring',
      'Inventory management system',
      'Pick and pack services',
      'Distribution services',
      'Flexible contract terms',
    ],
    popular: false,
    color: 'accent',
  },
  {
    id: 'customs',
    name: 'Customs Clearance',
    icon: 'DocumentCheckIcon',
    description: 'Expert handling of international shipments',
    basePrice: '$75',
    priceUnit: 'per shipment',
    features: [
      'Documentation preparation',
      'Compliance verification',
      'Fast processing (1-2 days)',
      'Duty and tax calculation',
      'Broker services',
      'Regulatory updates',
    ],
    popular: false,
    color: 'primary',
  },
];

export default function PricingTiers() {
  const handleGetStarted = (tierName: string) => {
    analytics.trackUserAction('pricing_tier_selected', { tier: tierName });
    analytics.trackConversion('pricing_tier_interest');
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Service Pricing Tiers
          </h2>
          <p className="text-lg text-muted-foreground">
            Flexible pricing options tailored to your shipping volume and needs
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {pricingTiers.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-card rounded-xl p-6 border-2 transition-all duration-300 hover:scale-105 hover:shadow-card-hover ${
                tier.popular
                  ? 'border-secondary shadow-lg'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-secondary text-secondary-foreground text-sm font-semibold rounded-full">
                  Most Popular
                </div>
              )}

              {/* Icon */}
              <div
                className={`w-14 h-14 rounded-lg flex items-center justify-center mb-4 ${
                  tier.color === 'secondary' ?'bg-secondary/10'
                    : tier.color === 'accent' ?'bg-accent/10' :'bg-primary/10'
                }`}
              >
                <Icon
                  name={tier.icon as any}
                  size={28}
                  className={`${
                    tier.color === 'secondary' ?'text-secondary'
                      : tier.color === 'accent' ?'text-accent' :'text-primary'
                  }`}
                />
              </div>

              {/* Tier Name */}
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                {tier.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">{tier.description}</p>

              {/* Pricing */}
              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-foreground">{tier.basePrice}</span>
                  <span className="text-sm text-muted-foreground">/{tier.priceUnit}</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                    <Icon name="CheckIcon" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => handleGetStarted(tier.name)}
                className={`w-full px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105 ${
                  tier.popular
                    ? 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            All prices are base rates. Final costs may vary based on weight, dimensions, and destination.
            <br />
            Volume discounts available for regular shippers.
          </p>
        </div>
      </div>
    </section>
  );
}